// @flow
import classnames from 'classnames';
import React from 'react';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import { withStyles, withTheme } from '@material-ui/core/styles';
import {
  Tooltip,
  Dialog,
  DialogContent,
  Typography,
  Button,
  DialogActions,
  TextField,
  Popover,
} from '@material-ui/core';
import isEqual from 'lodash/isEqual';

import { withSnackbar } from 'notistack';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { resetSelectedCaptions } from '../actions/session';
import { getSocketIfExist } from '../utils/getSocket';
import { sendUpdateTranscriptOwnerMessage, sendForceMute, sendTypeOnly } from '../utils/ws-v1';
import AvaMic from '../../assets/icons/mic-ava.png';
import { getSnack } from '../utils/snackbar';
import ScribeImg from '../../assets/images/scribe.svg';

import Transition from './Transition';

import AvatarLogo from './Icons/AvatarLogo';
import { beginChatWithUser } from '../services/api/ava';
import { differingKeys } from '../utils/object';

const participantPaperProps = {
  style: {
    marginTop: 8,
    marginLeft: 0,
    height: 32,
    background: 'transparent',
  },
};

const styles = theme => ({
  badgeRed: {
    backgroundColor: 'red',
    marginTop: 5,
    marginRight: 5,
  },
  badgeGreen: {
    backgroundColor: 'green',
    marginTop: 5,
    height: 15,
    minWidth: 15,
    width: 15,
  },
  cancelScribeButton: {
    fontWeight: 700,
    padding: '6px 12px',
    textTransform: 'none',
  },
  cancelScribeButtonCCMode: {
    padding: 4,
    height: 32,
    background: 'rgba(0, 0, 0, 0.5)',
    backgroundBlendMode: 'overlay',
    backdropFilter: 'blur(50px)',
    borderRadius: 4,
    fontWeight: 400,
    WebkitAppRegion: 'no-drag',
    '&:hover': {
      background: theme.palette.ava.grey5,
    },
  },
  dot: {
    backgroundColor: 'green',
    marginTop: 5,
    marginRight: 5,
  },
  noBadge: {
    display: 'none',
  },
  micContainer: {
    backgroundColor: theme.palette.ava.blue,
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 10,
    textAlign: 'center',
    '&.muted': {
      backgroundColor: theme.palette.ava.grey,
    },
  },
  micAvatar: {
    bottom: 0,
    right: -2,
  },
  micAvatarLogo: {
    bottom: 2,
    right: -2,
  },
  avatar: {
    margin: 3,
    borderStyle: 'solid',
    color: 'white',
    fontSize: 26,
    borderWidth: 2,
  },
  big: {
    height: 86,
    width: 86,
  },
  listened: {
    borderRightColor: `${theme.palette.ava.green} !important`,
    borderBottomColor: `${theme.palette.ava.green} !important`,
    boxShadow: `0px 0px 5px 3px ${theme.palette.ava.green}`,
  },
});

type Props = {
  audio: boolean,
  badge?: Boolean,
  classes: Object,
  canEdit?: boolean,
  canMute?: boolean,
  compact?: boolean,
  electronCaptionMode: boolean,
  index?: number,
  listened?: boolean,
  muted: boolean,
  nbOfConnection: number,
  size?: string,
  showBubbleAboveCCMode: boolean,
  user: Object,
  scribe?: string,
  t: Function,
  resetSelectedCaptions: Function,
  paidASRCreditTime: number,
  currentUser: Object,
  theme: Object,
};
type State = {
  cancelScribePopoverOpen: boolean,
  participantPopoverOpen: boolean,
  edit: boolean,
  title: string,
  userName: string,
};

// eslint-disable-next-line react/prefer-stateless-function
class ParticipantAvatar extends React.Component<Props, State> {
  avatarRef;

  constructor(props) {
    super(props);
    this.state = {
      cancelScribePopoverOpen: false,
      participantPopoverOpen: false,
      edit: false,
      userName: props.user ? props.user.userName : '',
      title: props.user ? props.user.title : '',
    };
    this.avatarRef = React.createRef();
  }

  shouldComponentUpdate(nextProps, nextState) {
    const differingProps = differingKeys(this.props, nextProps);
    const differingState = differingKeys(this.state, nextState);
    if (differingState.length > 0) {
      return true;
    }

    if (differingProps.length === 0) return false;
    if (differingProps.length > 1) {
      return true;
    }
    if (differingProps.length === 1) {
      if (differingProps[0] !== 'user') {
        return true;
      }
      // The only thing that changed is `user`. This is fairly common, as the
      // user object is recreated on every room-status-update. That's why we
      // only update if something important changed.
      const differingUserKeys = differingKeys(this.props.user, nextProps.user);
      let shallowDifferences = differingUserKeys.length;
      // `flags` and `theme` are objects, but are fairly shallow, so we can compare
      // them deeply. If any of them changed, or any other property is different
      // then we render the component.
      if (differingUserKeys.includes('flags')) {
        if (!isEqual(this.props.user.flags, nextProps.user.flags)) {
          return true;
        }
        shallowDifferences--;
      }
      if (differingUserKeys.includes('theme')) {
        if (!isEqual(this.props.user.theme, nextProps.user.theme)) {
          return true;
        }
        shallowDifferences--;
      }
      return shallowDifferences > 0;
    }
    return true;
  }

  componentDidUpdate() {
    const { electronCaptionMode, showBubbleAboveCCMode } = this.props;
    const { participantPopoverOpen } = this.state;

    // If mouse leaves CC conversation view, the participants are hidden
    if (electronCaptionMode && !showBubbleAboveCCMode && participantPopoverOpen) {
      this.setState({ participantPopoverOpen: false });
    }
  }

  dispatchSnack(message) {
    getSnack(this.props)(message);
  }

  update() {
    const { user } = this.props;
    const { userName, title } = this.state;
    const ws = getSocketIfExist();
    sendUpdateTranscriptOwnerMessage(ws, {
      avaId: user.id,
      userName,
      title,
    });
    this.setState({ edit: false });
  }

  handleNameChange = (ev: any) => {
    this.setState({ userName: ev.target.value });
  };

  handleTitleChange = (ev: any) => {
    this.setState({ title: ev.target.value });
  };

  avatarSrc(user: any) {
    const { scribe } = this.props;

    if (scribe) {
      return ScribeImg;
    }

    return user.userAvatar || user.photoUrl;
  }

  cancelScribe() {
    const ws = getSocketIfExist();
    /* eslint-disable no-shadow */
    const { scribe, t, resetSelectedCaptions, paidASRCreditTime } = this.props;

    if (ws) {
      const message = scribe === 'ongoing' ? t('scribe.3dRemove') : t('scribe.3dCancel');

      sendTypeOnly(ws, 'end-scribe');
      resetSelectedCaptions(paidASRCreditTime);

      this.dispatchSnack(message);
    } else {
      this.dispatchSnack(t('scribe.3dCancelFailed'));
    }
  }

  handleClick() {
    const { canEdit, canMute, electronCaptionMode, muted, scribe } = this.props;
    const { participantPopoverOpen } = this.state;
    if (canEdit || (canMute && !muted)) {
      const updatedParticipantPopoverOpen = !participantPopoverOpen;
      this.setState({ participantPopoverOpen: updatedParticipantPopoverOpen });
    } else if (scribe) {
      this.setState(prevState => {
        const { cancelScribePopoverOpen } = prevState;
        return { cancelScribePopoverOpen: !cancelScribePopoverOpen };
      });
    }
  }

  handleMute() {
    const { user } = this.props;
    sendForceMute(getSocketIfExist(), user.avaId);
  }

  async handleBeginChat() {
    const { currentUser, user, t } = this.props;
    const result = await beginChatWithUser(user.avaId, t('scribe.beginChatMessage'), currentUser.firebaseAuthUID);
    const conversationId = result.data.conversation_id;
    window.open(`https://app.intercom.com/a/apps/eko73i1t/inbox/conversation/${conversationId}`, '_blank', {
      popup: true,
    });
  }

  render() {
    const {
      audio,
      badge,
      canEdit,
      canMute,
      classes,
      compact,
      electronCaptionMode,
      index,
      listened,
      muted,
      nbOfConnection,
      size,
      showBubbleAboveCCMode,
      user,
      scribe,
      t,
      currentUser,
      theme,
    } = this.props;
    const { cancelScribePopoverOpen, participantPopoverOpen, edit } = this.state;

    let tooltip;

    if (scribe === 'pending') {
      tooltip = t('scribe.tooltipCancel');
    } else if (scribe === 'ongoing') {
      tooltip = t('scribe.tooltipRemove');
    } else if (window.isElectron && electronCaptionMode) {
      tooltip = user.userName;
    } else {
      tooltip = user.avaName ? `${user.userName} (&${user.avaName})` : `${user.userName} (guest)`;
    }

    if (typeof index === 'number' && !window.isElectron && !electronCaptionMode) {
      tooltip += ` - ${index}`;
    }
    let badgeClasse = classes.dot;
    if (!compact && nbOfConnection) {
      badgeClasse = classes.badgeGreen;
    } else if (!compact) {
      badgeClasse = classes.badgeRed;
    }
    if (!badge) {
      badgeClasse = classes.noBadge;
    }
    const borderColor = (user.theme && user.theme[`${theme.palette.type}_theme`].text_color) || '#EEEEEE';

    const tooltipHasContent = !cancelScribePopoverOpen && !participantPopoverOpen;
    const shouldShowTooltip =
      window.isElectron && electronCaptionMode ? showBubbleAboveCCMode && tooltipHasContent : tooltipHasContent;

    return (
      <div>
        {edit && (
          <form
            onSubmit={() => {
              this.update();
            }}
          >
            <Dialog open TransitionComponent={Transition} onClose={() => this.setState({ edit: false })}>
              <DialogContent
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h6">Edit participant info</Typography>
                <TextField
                  defaultValue={user.userName}
                  label="userName"
                  onChange={this.handleNameChange}
                  autoFocus
                  id="userName"
                  style={{ maxWidth: 300 }}
                  margin="normal"
                  onKeyDown={ev => {
                    if (['Enter'].includes(ev.key)) {
                      this.update();
                    }
                  }}
                />
                <TextField
                  defaultValue={user.title}
                  label="title"
                  onChange={this.handleTitleChange}
                  id="title"
                  style={{ minWidth: 300 }}
                  margin="normal"
                  onKeyDown={ev => {
                    if (['Enter'].includes(ev.key)) {
                      this.update();
                    }
                  }}
                />
                <a
                  href={`https://ava-parse-dashboard.herokuapp.com/apps/Ava%20Parse%20Prod/browser/Account?filters=%5B%7B%22field%22%3A%22avaId%22%2C%22constraint%22%3A%22eq%22%2C%22compareTo%22%3A%22${user.id}%22%7D%5D`}
                  // eslint-disable-next-line
                  target="_blank"
                  rel="noreferrer"
                >
                  {' '}
                  Update picture on parse{' '}
                </a>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  onClick={() =>
                    this.setState({
                      edit: false,
                      userName: (user || {}).userName,
                      title: (user || {}).title,
                    })
                  }
                >
                  Cancel
                </Button>
                <Button
                  // disabled={submitting || !this.isValid()}
                  variant="contained"
                  type="submit"
                  onClick={() => this.update()}
                  color="primary"
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </form>
        )}
        <Tooltip title={shouldShowTooltip ? tooltip : ''} aria-label="share-link" enterDelay={300} leaveDelay={200}>
          <Badge
            ref={this.avatarRef}
            badgeContent={nbOfConnection}
            color="secondary"
            classes={{ badge: badgeClasse }}
            onClick={() => this.handleClick()}
            variant={!nbOfConnection || nbOfConnection === 1 ? 'dot' : 'standard'}
          >
            {user && this.avatarSrc(user) ? (
              <Avatar
                aria-label="user-avatar"
                src={this.avatarSrc(user)}
                style={{
                  borderColor,
                  backgroundColor: borderColor,
                  borderRightColor: borderColor,
                  borderBottomColor: borderColor,
                  cursor: canEdit || canMute || scribe ? 'pointer' : 'inherit',
                  WebkitMaskImage: '-webkit-radial-gradient(white, black)',
                }}
                className={`${classes.avatar} ${size === 'big' ? classes.big : ''} ${listened ? classes.listened : ''}
                ${scribe === 'pending' ? 'flash' : ''}
                ${scribe ? 'pointer' : ''}`}
              >
                {((user.userName || '')[0] || 'g').toUpperCase()}
              </Avatar>
            ) : (
              <div
                className={classnames(classes.avatar, {
                  [classes.listened]: listened,
                  flash: scribe === 'pending',
                  pointer: scribe,
                })}
                style={{
                  borderStyle: 'none',
                  cursor: canEdit || canMute || scribe ? 'pointer' : 'inherit',
                  WebkitMaskImage: '-webkit-radial-gradient(white, black)',
                  height: size === 'big' ? 92 : 44,
                  width: size === 'big' ? 92 : 44,
                }}
              >
                <AvatarLogo
                  // eslint-disable-next-line no-nested-ternary
                  color={
                    currentUser.avaId === user.avaId
                      ? theme.palette.type === 'light'
                        ? theme.palette.ava.navyGroup.convoText
                        : theme.palette.ava.navyGroup.convoTextDark
                      : borderColor
                  }
                  width={size === 'big' ? 92 : 44}
                  height={size === 'big' ? 92 : 44}
                />
              </div>
            )}
            {audio && (
              <div
                className={classnames(
                  classes.micContainer,
                  { [classes.micAvatar]: user && this.avatarSrc(user) },
                  { [classes.micAvatarLogo]: !user || !this.avatarSrc(user) },
                  { muted: muted }
                )}
              >
                <img src={AvaMic} alt="ava_mic" height="12" />
              </div>
            )}
            {currentUser.avaId !== user.avaId && (
              <Popover
                open={electronCaptionMode ? showBubbleAboveCCMode && participantPopoverOpen : participantPopoverOpen}
                anchorEl={this.avatarRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={participantPaperProps}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {canEdit && (
                    <Button
                      className={classnames(classes.cancelScribeButton, {
                        [classes.cancelScribeButtonCCMode]: window.isElectron && electronCaptionMode,
                      })}
                      onClick={() => this.setState({ edit: true })}
                    >
                      {t('avatar.edit')}
                    </Button>
                  )}
                  {canMute && !muted && (
                    <Button
                      className={classnames(classes.cancelScribeButton, {
                        [classes.cancelScribeButtonCCMode]: window.isElectron && electronCaptionMode,
                      })}
                      onClick={() => this.handleMute()}
                    >
                      {t('avatar.mute')}
                    </Button>
                  )}
                  <Button onClick={() => this.handleBeginChat()}>{t('scribe.beginChat')}</Button>
                </div>
              </Popover>
            )}
            {scribe && (
              <Popover
                open={electronCaptionMode ? showBubbleAboveCCMode && cancelScribePopoverOpen : cancelScribePopoverOpen}
                anchorEl={this.avatarRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={participantPaperProps}
              >
                <Button
                  className={classnames(classes.cancelScribeButton, {
                    [classes.cancelScribeButtonCCMode]: window.isElectron && electronCaptionMode,
                  })}
                  onClick={() => this.cancelScribe()}
                >
                  {scribe === 'ongoing' ? t('scribe.remove') : t('scribe.cancel')}
                </Button>
              </Popover>
            )}
          </Badge>
        </Tooltip>
      </div>
    );
  }
}

const mapStateToProps = ({
  ccMode: { showBubbleAboveCCMode },
  firebase: { paidASRCreditTime },
  scribeSession: { captioner },
  scribeTranscriptUI: { electronCaptionMode },
}) => ({
  electronCaptionMode,
  paidASRCreditTime,
  currentUser: captioner,
  showBubbleAboveCCMode,
});

const mapDispatchToProps = {
  resetSelectedCaptions,
};

export default withTranslation()(
  withSnackbar(withStyles(styles)(withTheme(connect(mapStateToProps, mapDispatchToProps)(ParticipantAvatar))))
);
